/**
 * +field: character creation's pure glue: name validation that mirrors progression's
 * sanitizeHeroName (the intent re-checks it) with an inline reason per rule, and the class cards.
 */
import { HERO_NAME_MAX, HERO_NAME_MIN, sanitizeHeroName } from '@keystone/progression';

export type NameCheck = { ok: true; name: string } | { ok: false; error: string };

/** Validate a typed name; `ok` carries the cleaned name the character will get. */
export function checkHeroName(raw: string): NameCheck {
  const s = raw.normalize('NFC').trim().replace(/\s+/g, ' ');
  if (!s) return { ok: false, error: 'Choose a name for your hero.' };
  if (s.length < HERO_NAME_MIN) return { ok: false, error: `At least ${HERO_NAME_MIN} letters.` };
  if (s.length > HERO_NAME_MAX) return { ok: false, error: `At most ${HERO_NAME_MAX} characters.` };
  if (/\d/.test(s)) return { ok: false, error: 'Letters only: no numbers.' };
  if (/[^\p{L} '\-]/u.test(s)) return { ok: false, error: 'Letters only, with single spaces, hyphens or apostrophes.' };
  if (/[ '\-]{2,}|^[ '\-]|[ '\-]$/u.test(s)) return { ok: false, error: 'Spaces, hyphens and apostrophes go between letters.' };
  const name = sanitizeHeroName(s);
  return name ? { ok: true, name } : { ok: false, error: 'That name is not allowed.' };
}

export interface ClassCard {
  classId: string;
  name: string;
  role: string;
  blurb: string;
}

const BLURB: Readonly<Record<string, string>> = {
  // +classes: the five base classes.
  warrior: 'A shield and a blade. Takes the hits for the group, hews through packs and gets back up on her own.',
  acolyte: 'A healer who fights with light: every lash mends an ally, and her circle heals friends and sears foes.',
  rogue: 'Quick and slippery. Builds an edge with fast cuts, then ends the fight with a flourish.',
  mage: 'Raw arcane power from range: charged darts, a piercing lance and flares that burst whole groups.',
  ranger: 'A patient archer. Fires on the move, rains arrows on packs and snares anything that gets too close.',
  warden: 'A shield-bearing guardian. Hard to kill, holds enemies on herself and grinds steadily through packs.',
  lightbinder: 'A healer of dawnlight. Mends her own wounds between strikes of holy fire; slow and sure.',
  blademaster: 'A swift swordswoman. Builds momentum with combos and cuts down single foes fastest.',
  stormcaller: 'A caster of lightning and flame. Strikes from range and blasts whole groups at once.',
};

export function classBlurb(classId: string): string {
  return BLURB[classId] ?? '';
}
